
import { initTheme, setTheme, disposeTheme } from '@/unit/theme'
import { fetchPublicConfig, resolveShopShortName } from '@/utils/publicConfig'
import { APP_CONFIG } from '@/config'

export default {
  name: 'AdminLayout',
  data () {
    return {
      isCollapsed: false,
      theme: 'system',
      themeMenuOpen: false,
      themeMenuReady: false,
      settingsMenuOpen: false, // 账号设置（齿轮）下拉
      // 侧栏品牌位：默认系统品牌，publicConfig 到货后换店铺短名（简称→店铺名称）
      brandTitle: APP_CONFIG.systemBrandName,
      themeModes: [
        { value: 'system', label: '跟随系统' },
        { value: 'light', label: '浅色' },
        { value: 'dark', label: '深色' }
      ],
      // 账号安全（改密/改号）弹窗状态
      showPwdModal: false,
      showPhoneModal: false,
      pwdForm: { old: '', new: '', confirm: '' },
      phoneForm: { new_phone: '', current_password: '' },
      savingPwd: false,
      savingPhone: false
    }
  },
  created () {
    this.checkAdmin()
    this.getUserInfo()
    // 侧栏品牌位读店铺短名（resolveShopShortName 单一收口；未配置回落系统品牌 RoviaSys）。
    // 改配置后刷新生效（与前台同语义），不做会话内热更新
    fetchPublicConfig().then(cfg => {
      this.brandTitle = resolveShopShortName(cfg) || APP_CONFIG.systemBrandName
    }).catch(() => { /* 拉取失败保持品牌默认 */ })
    // 主题：main.js 已在 /admin 刷新时早期挂载防闪白；这里补 system 监听并同步状态
    this.theme = initTheme()
  },
  mounted () {
    document.addEventListener('click', this.closeThemeMenu)
    this.themeMenuReady = true
  },
  beforeDestroy () {
    document.removeEventListener('click', this.closeThemeMenu)
    // 离开 Admin：摘除 .dark 与 system 监听，避免污染未 token 化的 C 端页面
    disposeTheme()
  },
  computed: {
    // 使用 store 的统一用户信息
    userInfo () {
      return this.$store.getters.currentUser || {}
    },
    isAdmin () {
      return this.$store.getters.isAdmin
    },
    isSuperAdmin () {
      return this.$store.getters.isSuperAdmin
    },
    pageTitle () {
      return (this.$route.meta && this.$route.meta.title) || '后台管理'
    },
    adminName () {
      return this.userInfo.nickname || this.userInfo.phone || ''
    },
    themeLabel () {
      const m = this.themeModes.find(x => x.value === this.theme)
      return m ? m.label : '跟随系统'
    }
  },
  methods: {
    toggleCollapse () {
      this.isCollapsed = !this.isCollapsed
    },
    checkAdmin () {
      // 使用 store 中的用户信息，不再依赖本地缓存
      if (!this.$store.getters.isAdmin) {
        this.$router.push('/')
      }
    },
    getUserInfo () {
      // 用户信息从 computed 属性获取，无需额外处理
    },
    chooseTheme (mode) {
      setTheme(mode)
      this.theme = mode
      this.themeMenuOpen = false
    },
    // 外点关闭：主题 + 账号设置两个下拉一起收（各自容器 @click.stop 隔离冒泡）
    closeThemeMenu () {
      this.themeMenuOpen = false
      this.settingsMenuOpen = false
    },
    async logout () {
      // Phase B：先撤服务端 session（不只删前端 token）；失败也照常清本地（滑动过期兜底）
      try { await this.axios.post('/auth/logout') } catch (_) {}
      this.$store.commit('clearUser')
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      // 登出瞬间 token 已清：守卫可能把 '/' 重定向（如残留 redirect 链），vue-router 3 会
      // 把守卫重定向的 push promise reject 成 unhandled rejection——catch 掉这层导航噪音
      this.$router.push('/').catch(() => {})
    },
    backToMobile () {
      this.$router.push('/home')
    },
    // Admin Lite：移动端轻管理台（/admin-lite/ 独立静态页），不在 Vue Router 内——整页跳转
    gotoAdminLite () {
      window.location.href = '/admin-lite/'
    },
    // ===== 账号安全（提交逻辑与移动端 Account.vue 同源） =====
    openPwdModal () {
      this.settingsMenuOpen = false
      this.pwdForm = { old: '', new: '', confirm: '' }
      this.showPwdModal = true
    },
    openPhoneModal () {
      this.settingsMenuOpen = false
      this.phoneForm = { new_phone: '', current_password: '' }
      this.showPhoneModal = true
    },
    // 改密成功 = token_version 已自增，旧 token 全失效 → 清登录态回登录页（清态范围与 logout 一致）
    async submitPassword () {
      if (!this.pwdForm.old || !this.pwdForm.new || !this.pwdForm.confirm) {
        this.$q.notify({ type: 'negative', message: '请填写完整' }); return
      }
      if (this.pwdForm.new !== this.pwdForm.confirm) {
        this.$q.notify({ type: 'negative', message: '两次输入的密码不一致' }); return
      }
      this.savingPwd = true
      try {
        const res = await this.axios.post('/user/change-password', {
          old_password: this.pwdForm.old, new_password: this.pwdForm.new, confirm_password: this.pwdForm.confirm
        })
        if (res.data.code === 200) {
          this.$q.notify({ type: 'positive', message: '密码修改成功，请重新登录' })
          this.showPwdModal = false
          setTimeout(() => {
            this.$store.commit('clearUser')
            localStorage.removeItem('token')
            this.$router.push('/')
          }, 1200)
        } else {
          this.$q.notify({ type: 'negative', message: res.data.message })
        }
      } catch (e) {
        this.$q.notify({ type: 'negative', message: (e.response && e.response.data && e.response.data.message) || '修改失败' })
      }
      this.savingPwd = false
    },
    // 改号保 users.id 不变（后端 UPDATE WHERE id），登录态不断（token_version 不动）；成功后 refreshUser 同步 store，顶栏 adminName 跟随
    async submitPhone () {
      if (!this.phoneForm.new_phone || !this.phoneForm.current_password) {
        this.$q.notify({ type: 'negative', message: '请填写完整' }); return
      }
      this.savingPhone = true
      try {
        const res = await this.axios.post('/user/me/phone', this.phoneForm)
        if (res.data.code === 200) {
          this.$q.notify({ type: 'positive', message: '手机号修改成功' })
          this.showPhoneModal = false
          this.$store.dispatch('refreshUser')
        } else {
          this.$q.notify({ type: 'negative', message: res.data.message })
        }
      } catch (e) {
        this.$q.notify({ type: 'negative', message: (e.response && e.response.data && e.response.data.message) || '修改失败' })
      }
      this.savingPhone = false
    }
  }
}
